import { ApolloCache, NormalizedCacheObject } from '@apollo/client/core';
import {
    NodeTypename,
    ObjectType,
} from '@precon-tools-suite-by-shared/precon-graphql-types';

/**
 * Removes references from a field with an array of references.
 *
 * @param cache - The cache hosting the item.
 * @param id - The ID of the cache item to remove the references from.
 * @param fieldName - The field to remove the references from.
 * @param references - The reference IDs to remove.
 * @returns {string[]} - The list of references that were removed.
 */
export function removeReferences<N extends NodeTypename>(
    cache: ApolloCache<NormalizedCacheObject>,
    id: string,
    fieldName: keyof ObjectType<N>,
    references: string[]
) {
    const removedReferences: string[] = [];
    cache.modify({
        id: id,
        fields: {
            [fieldName](existing: any) {
                const filteredReferences = existing?.edges?.filter((e) => {
                    const referenceIndex = references.findIndex(
                        (r) => e.node.__ref === r
                    );
                    if (referenceIndex !== -1) {
                        removedReferences.push(references[referenceIndex]);
                        return false;
                    }
                    return true;
                });

                if (
                    filteredReferences != null &&
                    filteredReferences !== undefined
                ) {
                    return {
                        ...existing,
                        edges: filteredReferences,
                    };
                } else {
                    return existing;
                }
            },
        },
    });
    return removedReferences;
}

/**
 * Restores references to a field with an array of references.
 *
 * @param cache - The cache hosting the item.
 * @param id - The ID of the cache item to restore the references to.
 * @param fieldName - The field to restore the references to.
 * @param references - The reference IDs to restore.
 */
export function restoreReferences<N extends NodeTypename>(
    cache: ApolloCache<any>,
    id: string,
    fieldName: keyof ObjectType<N>,
    references: string[]
) {
    cache.modify({
        id: id,
        fields: {
            [fieldName](existing: any) {
                const restoredReferences = existing?.edges?.concat(
                    references.map((x) => ({ node: { __ref: x } }))
                );

                if (
                    restoredReferences != null &&
                    restoredReferences !== undefined
                ) {
                    return {
                        ...existing,
                        edges: restoredReferences,
                    };
                } else {
                    return existing;
                }
            },
        },
    });
}
